package com.bikompanion.ui.home

import androidx.compose.animation.core.LinearEasing
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import dev.chrisbanes.haze.HazeInput
import dev.chrisbanes.haze.HazeProgressive
import dev.chrisbanes.haze.HazeSourceSelection
import dev.chrisbanes.haze.HazeState
import dev.chrisbanes.haze.blur.HazeBlurStyle
import dev.chrisbanes.haze.blur.hazeBlur
import com.bikompanion.ui.preview.GlassPreviewSurface
import com.bikompanion.ui.preview.PreviewLightDarkCompact
import com.bikompanion.ui.preview.PreviewLightDarkLandscape

private val EdgeBlurMaxHeight = 160.dp
private val EdgeBlurMinHeight = 40.dp
private val EdgeBlurMaxRadius = 32.dp
private val EdgeBlurMinRadius = 12.dp

// Each band takes this share of the window height, so the two together stay under 40%.
private const val EdgeBlurHeightFraction = 0.2f

/** Softens map detail at the system edges while leaving controls above it crisp. */
@Composable
internal fun ScreenEdgeBlur(
    hazeState: HazeState,
    modifier: Modifier = Modifier,
) {
    BoxWithConstraints(modifier = modifier) {
        // Fixed 160dp bands covered ~90% of a phone in landscape, smearing the whole map instead
        // of softening its edges. The radius shrinks with the band so it still fades out inside it.
        val bandHeight = (maxHeight * EdgeBlurHeightFraction)
            .coerceIn(EdgeBlurMinHeight, EdgeBlurMaxHeight)
        val blurRadius = (EdgeBlurMaxRadius * (bandHeight / EdgeBlurMaxHeight))
            .coerceAtLeast(EdgeBlurMinRadius)

        EdgeBlur(
            hazeState = hazeState,
            height = bandHeight,
            radius = blurRadius,
            intensityAtTop = 0.25f,
            intensityAtBottom = 0f,
            modifier = Modifier.align(Alignment.TopCenter),
        )
        EdgeBlur(
            hazeState = hazeState,
            height = bandHeight,
            radius = blurRadius,
            intensityAtTop = 0f,
            intensityAtBottom = 0.25f,
            modifier = Modifier.align(Alignment.BottomCenter),
        )
    }
}

@Composable
private fun EdgeBlur(
    hazeState: HazeState,
    height: Dp,
    radius: Dp,
    intensityAtTop: Float,
    intensityAtBottom: Float,
    modifier: Modifier = Modifier,
) {
    val blurStyle = remember(radius, intensityAtTop, intensityAtBottom) {
        HazeBlurStyle {
            blurRadius(radius)
            noiseFactor(1f)
            progressive(
                HazeProgressive.verticalGradient(
                    easing = LinearEasing,
                    startIntensity = intensityAtTop,
                    endIntensity = intensityAtBottom,
                ),
            )
        }
    }
    Box(
        modifier = modifier
            .fillMaxWidth()
            .height(height)
            .hazeBlur(
                // This lives outside the map's hazeSource, where the default `Behind`
                // selection would include every source. Limit it to map-level sources so the
                // full-width edge layer cannot blur the rounded nav/action into a rectangle.
                input = HazeInput.Sources(
                    state = hazeState,
                    selection = HazeSourceSelection.All.where { it.zIndex < 1f },
                ),
                style = blurStyle,
            ),
    )
}

@PreviewLightDarkCompact
@Composable
private fun ScreenEdgeBlurPreview() {
    GlassPreviewSurface { hazeState ->
        ScreenEdgeBlur(hazeState = hazeState, modifier = Modifier.fillMaxSize())
    }
}

/** Short enough for the bands to hit their minimum height, which is what the fractions are for. */
@PreviewLightDarkLandscape
@Composable
private fun ScreenEdgeBlurLandscapePreview() {
    GlassPreviewSurface { hazeState ->
        ScreenEdgeBlur(hazeState = hazeState, modifier = Modifier.fillMaxSize())
    }
}
